import { useEffect, useMemo, useRef, useState } from 'react'
import { SmilePlus } from 'lucide-react'

const DEFAULT_REACTION_EMOJIS = ['👍', '❤️', '🎉', '🤔', '⚠️', '🚀', '🐛', '📌']

/**
 * Emoji reaction strip + hover-revealed picker.
 *
 * The picker open state is JS-controlled with a close delay so moving the mouse
 * from the trigger to the picker (across the small gap) doesn't dismiss it.
 *
 * reactions: [{ emoji, user_id }]  onToggle(emoji)
 * size: 'normal' | 'sm' (compact, for per-comment rows)
 */
export default function ReactionsBar({
    reactions,
    currentUserId,
    onToggle,
    size = 'normal',
    emojis = DEFAULT_REACTION_EMOJIS,
}) {
    const grouped = useMemo(() => {
        const map = {}
        for (const r of reactions || []) {
            if (!map[r.emoji]) map[r.emoji] = { count: 0, byMe: false }
            map[r.emoji].count++
            if (r.user_id === currentUserId) map[r.emoji].byMe = true
        }
        return map
    }, [reactions, currentUserId])

    const [open, setOpen] = useState(false)
    const timer = useRef(null)
    const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null } }
    const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 280) }
    useEffect(() => () => cancelClose(), [])

    return (
        <div className={`reactbar ${size === 'sm' ? 'reactbar-sm' : ''}`.trim()}>
            {emojis.map(emoji => {
                const g = grouped[emoji]
                if (!g) return null
                return (
                    <button
                        key={emoji}
                        type="button"
                        className={`reactbar-pill ${g.byMe ? 'active' : ''}`.trim()}
                        onClick={() => onToggle(emoji)}
                    >
                        <span>{emoji}</span>
                        <span className="reactbar-count">{g.count}</span>
                    </button>
                )
            })}
            <div
                className="reactbar-add"
                onMouseEnter={() => { cancelClose(); setOpen(true) }}
                onMouseLeave={scheduleClose}
            >
                <button
                    type="button"
                    className="reactbar-add-trigger"
                    aria-label="Adaugă reacție"
                    title="Adaugă reacție"
                    onClick={() => setOpen(o => !o)}
                >
                    <SmilePlus size={14} />
                </button>
                {open && (
                    <div
                        className="reactbar-picker"
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        {emojis.map(emoji => (
                            <button
                                key={emoji}
                                type="button"
                                className="reactbar-pick"
                                onClick={() => { onToggle(emoji); setOpen(false) }}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
